import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { EditorSelection, Value } from '@platejs/slate';
import {
  createPlatePlugin,
  Plate,
  PlateContent,
  usePlateEditor,
} from 'platejs/react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { GripVertical } from '@/lib/base/foundations/grip-vertical';
import { insertReplacementTextPlugin } from '@/lib/crm/components/insert-replacement-text-plugin';
import { cx } from '@/utils/cx';

import { TokenBadge } from './TokenBadge';
import { TokenElement } from './TokenElement';
import type { EventNameToken } from './tokenTemplate';
import {
  EVENT_NAME_TOKENS,
  plateValueToTemplate,
  templateToPlateValue,
} from './tokenTemplate';

export interface EventNameTemplateEditorProps {
  value: unknown;
  onChange: (next: unknown) => void;
  disabled: boolean;
}

const GUIDE_ID = 'event-name-template-guide';
const EDITOR_DROPPABLE_ID = 'event-name-template-editor-dropzone';

const GUIDE_TEXT =
  "Drag or click a token to insert it. Leave blank to use the default '{booking type} with {invitee}'.";

// Fully static — no feature flags gate this plugin — so it's hoisted to
// module scope. Rebuilding it inside the component would give usePlateEditor
// a new plugin identity on every render, which rebuilds the editor mid-interaction.
const TokenPlugin = createPlatePlugin({
  key: 'token',
  node: {
    isElement: true,
    isInline: true,
    isVoid: true,
  },
}).withComponent(TokenElement);

const TOKEN_EDITOR_PLUGINS = [insertReplacementTextPlugin, TokenPlugin];

const POINTER_SENSOR_ACTIVATION = { activationConstraint: { distance: 8 } };

// The drop zone MUST live in a component rendered *inside* <DndContext>.
// useDroppable reads dnd-kit's InternalContext, and a hook called in the same
// component that renders the provider sees `defaultInternalContext`, whose
// `dispatch` is a no-op — so the droppable registers into nothing, `over` is
// always null, and every drop is silently discarded while the drag itself
// still appears to work. Keep this as a child; do not inline it back.
function TemplateDropZone({ children }: { children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: EDITOR_DROPPABLE_ID });
  return (
    <div
      ref={setNodeRef}
      className={cx(
        'rounded-lg border border-primary',
        isOver && 'border-brand'
      )}
    >
      {children}
    </div>
  );
}

interface TokenPaletteButtonProps {
  token: EventNameToken;
  onInsert: (tokenName: string) => void;
  disabled: boolean;
}

function TokenPaletteButton({
  token,
  onInsert,
  disabled,
}: TokenPaletteButtonProps) {
  // preventDefault keeps the button from stealing focus so the editor's caret
  // is preserved on click.
  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    },
    []
  );

  // A native button fires `click` for both mouse click and keyboard Enter/Space,
  // so one handler covers both insert paths.
  const handleClick = useCallback(() => {
    onInsert(token.name);
  }, [onInsert, token.name]);

  // setNodeRef marks the whole pill as the draggable (so dnd-kit measures the
  // full chip rect for collision detection), while setActivatorNodeRef limits
  // what actually starts a drag to the grip. Without the split, a press-and-
  // drag anywhere on the label would still fire `click` on release and insert
  // a token the user was only trying to drag.
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useDraggable({
      id: token.name,
      disabled,
    });

  return (
    <span
      ref={setNodeRef}
      className={cx(
        'inline-flex items-center gap-0.5 align-middle',
        isDragging && 'opacity-50'
      )}
    >
      {!disabled && (
        <button
          ref={setActivatorNodeRef}
          type='button'
          // touch-none stops a touch-drag from being stolen by page scroll.
          className='flex cursor-grab items-center justify-center rounded touch-none focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
          aria-label={`Drag ${token.label} token into the template`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className='size-3.5 text-[var(--color-text-tertiary)]' />
        </button>
      )}
      <button
        type='button'
        disabled={disabled}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        className={cx(
          'inline-flex rounded-full align-middle',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
        )}
      >
        <TokenBadge label={token.label} />
      </button>
    </span>
  );
}

export const EventNameTemplateEditor: React.FC<
  EventNameTemplateEditorProps
> = ({ value, onChange, disabled }) => {
  const template = typeof value === 'string' ? value : '';

  const lastEmittedValueRef = useRef<string>(template);
  // Last-known caret, captured while the editor has focus/selection, so a
  // drag-drop insert (which loses editor selection while dragging) still
  // lands where the user last placed the cursor. A ref (not state) because
  // updating it must not re-render the editor on every cursor move.
  const lastCaretRef = useRef<EditorSelection>(null);
  const [activeToken, setActiveToken] = useState<EventNameToken | null>(null);

  const initialValue = useMemo(
    () => templateToPlateValue(template) as Value,
    [template]
  );

  const editor = usePlateEditor({
    plugins: TOKEN_EDITOR_PLUGINS,
    value: initialValue,
  });

  // Sync editor state when `value` changes externally. Guard against echoing
  // our own onChange — only push into the editor when the incoming template
  // differs from what we last emitted upward. Mirrors PlateNoteEditorImpl.tsx.
  useEffect(() => {
    const currentTemplate = typeof value === 'string' ? value : '';
    if (currentTemplate !== lastEmittedValueRef.current) {
      editor.tf.setValue(templateToPlateValue(currentTemplate) as Value);
      lastEmittedValueRef.current = currentTemplate;
    }
  }, [value, editor]);

  const handlePlateChange = useCallback(
    ({ value: newValue }: { value: Value }) => {
      if (disabled) {
        return;
      }
      const next = plateValueToTemplate(newValue);
      if (next !== lastEmittedValueRef.current) {
        lastEmittedValueRef.current = next;
        onChange(next);
      }
    },
    [disabled, onChange]
  );

  const insertTokenAtCaret = useCallback(
    (tokenName: string, caret: EditorSelection): boolean => {
      const target = caret ?? editor.selection ?? editor.api.end([]);
      try {
        editor.tf.select(target);
        editor.tf.insertNodes({
          type: 'token',
          tokenName,
          children: [{ text: '' }],
        });
        return true;
      } catch (error) {
        console.error(
          '[event-name-template] Failed to insert token at caret:',
          error
        );
        return false;
      }
    },
    [editor]
  );

  const handlePaletteInsert = useCallback(
    (tokenName: string) => {
      // Prefer the live selection, then the last caret we captured while the
      // editor had one. Passing null here (the previous behaviour) meant that
      // whenever clicking the palette had cleared the editor's selection, the
      // insert silently fell through to end-of-template and the token landed
      // at the end no matter where the user had put the cursor.
      const at = editor.selection ?? lastCaretRef.current;
      if (!insertTokenAtCaret(tokenName, at)) {
        toast.error('Could not insert token. Please try again.');
      }
    },
    [editor, insertTokenAtCaret]
  );

  // Record the caret whenever the editor has one, so an insert triggered from
  // outside the editor (palette click, drop) can still target it after the
  // interaction has taken the selection away. Only store real selections — a
  // null would overwrite a good caret with nothing.
  const handleCaretCapture = useCallback(() => {
    if (editor.selection) {
      lastCaretRef.current = editor.selection;
    }
  }, [editor]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveToken(
      EVENT_NAME_TOKENS.find(t => t.name === event.active.id) ?? null
    );
  }, []);

  // Resolve the point the user actually released over, so a token lands where
  // it was dropped instead of at the end of the template. dnd-kit gives the
  // press coordinates (activatorEvent) plus the total movement (delta); their
  // sum is the release point. Slate's findEventRange turns that into a range,
  // and it already does the right thing when the release lands on an existing
  // token: it resolves to the near edge, so dropping on the left half of a
  // token inserts before it and the right half after it.
  const findDropRange = useCallback(
    (event: DragEndEvent): EditorSelection => {
      const activator = event.activatorEvent as Partial<PointerEvent> | null;
      if (activator?.clientX == null || activator.clientY == null) {
        return null;
      }
      const clientX = activator.clientX + event.delta.x;
      const clientY = activator.clientY + event.delta.y;
      const editorEl = editor.api.toDOMNode(editor);
      if (!editorEl) {
        return null;
      }
      // elementsFromPoint (plural) so the floating DragOverlay sitting under
      // the cursor doesn't mask the editor — take the topmost element that is
      // actually inside the editable, which is what findEventRange requires.
      const target = document
        .elementsFromPoint(clientX, clientY)
        .find(el => editorEl.contains(el));
      if (!target) {
        return null;
      }
      return editor.api.findEventRange({ clientX, clientY, target }) ?? null;
    },
    [editor]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveToken(null);
      if (disabled || event.over?.id !== EDITOR_DROPPABLE_ID) {
        return;
      }
      // Drop point first; fall back to the last known caret, then (inside
      // insertTokenAtCaret) to the end of the template.
      const at = findDropRange(event) ?? lastCaretRef.current;
      if (!insertTokenAtCaret(String(event.active.id), at)) {
        toast.error('Could not insert token. Please try again.');
      }
    },
    [disabled, insertTokenAtCaret, findDropRange]
  );

  // A canceled drag (Escape, pointer lost) never fires onDragEnd, so the
  // overlay would stay stuck without this reset.
  const handleDragCancel = useCallback(() => {
    setActiveToken(null);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_SENSOR_ACTIVATION)
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className='flex flex-col gap-3'>
        <div className='flex flex-wrap gap-2'>
          {EVENT_NAME_TOKENS.map(token => (
            <TokenPaletteButton
              key={token.name}
              token={token}
              onInsert={handlePaletteInsert}
              disabled={disabled}
            />
          ))}
        </div>
        <TemplateDropZone>
          <Plate editor={editor} onChange={handlePlateChange}>
            <PlateContent
              readOnly={disabled}
              aria-label='Calendar event name template'
              aria-describedby={GUIDE_ID}
              onMouseUp={handleCaretCapture}
              onKeyUp={handleCaretCapture}
              className='min-h-10 w-full px-3 py-2 text-md focus:outline-none'
            />
          </Plate>
        </TemplateDropZone>
        <p id={GUIDE_ID} className={cx('text-body-small text-fg-tertiary')}>
          {GUIDE_TEXT}
        </p>
      </div>
      <DragOverlay>
        {activeToken ? <TokenBadge label={activeToken.label} /> : null}
      </DragOverlay>
    </DndContext>
  );
};
